import { injectable } from 'inversify';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { Communicator, Component, ObjectManager } from '../../../concepts/objects';
import { Matrix } from '../../../platform/geom/Matrix';
import { Rect } from '../../../platform/geom/Rect';
import { ActiveObject, resolve } from '../../Container';
import { Phys } from '../entities/Phys';
import { Effect, EffectParams, PoseAttr } from './Pose';

const createEffect = (name: string, duration: number, params: EffectParams = {}, cascade: boolean = false): Effect =>
    ({ name, duration, cascade, ...params });

export const Effects = {
    create: createEffect,
    fade: (value: number) => createEffect('fade', -1, { value }),
    flash: (rect: Rect, color?: string) => createEffect('flash', 1000, { rect, color }),
    popIn: createEffect('popIn', 300),
    raise: (value: number) => createEffect('raise', -1, { value }),
    shake: createEffect('shake', 500),
    shuffle: () => createEffect('shuffle', 1000, { value: Math.floor(Math.random() * 12) }),
    text: (value: string, position?: { x: number, y: number }, color?: string) => createEffect('text', 500, { value, position, color }),
    tween: (from: Matrix, to: Matrix) => createEffect('tween', 500, { from, to }, true),
};

@injectable()
export class EffectDirector extends ActiveObject {

    @resolve(Communicator) private comms!: Communicator;
    @resolve(ObjectManager) private objects!: ObjectManager;

    public start = () => this.burden(
        this.comms.recv('effect', (_, data) => {
            const [effect, targets] = data;
            const subjects = targets.map(x => this.objects.getMaybe(x)).filter(x => !!x);
            this.trigger(effect, subjects);
        }
        ));

    public send(effect: Effect, input: Component | Component[]) {
        input = Array.isArray(input) ? input : [input];
        const data = [effect, input.map(x => x.id)];
        this.comms.send('effect', data);
    }

    public async trigger(effect: Effect, input: Component | Component[]) {
        if (Array.isArray(input)) {
            await Promise.all(input.map(x => this.trigger(effect, x)));
        }
        else {
            if (effect.cascade) {
                await Promise.all(
                    Phys.from(input.flatten(true)).map(
                        x => this.triggerInternal(effect, x)
                    )
                );
            }
            else {
                await this.triggerInternal(effect, input);
            }
        }
    }

    public untrigger(effect: Effect | string, input: Component | Component[]) {
        const name = typeof effect === 'string' ? effect : effect.name;
        const components = Array.isArray(input) ? input : [input];

        for (const c of components) {
            const pose = PoseAttr.get(c);
            if (pose.effects && !pose.effects[name]) continue;
            const next = { ...(pose.effects ?? {}) };
            delete next[name];
            PoseAttr.set(c, { ...pose, effects: next });
        }
    }

    private async triggerInternal(effect: Effect, subject: Component): Promise<void> {
        const pose = PoseAttr.get(subject);
        // If effect already in effect (ehehe) we can't trigger so return false
        if (pose.effects && pose.effects[effect.name]) return;
        // Apply
        this.apply(effect, subject);
        // If effect has a duration, wait for it to finish and remove
        if (effect.duration > 0) {
            await waitFor(effect.duration);
            this.remove(effect, subject);
        }
    }

    private apply(effect: Effect, subject: Component) {
        const pose = PoseAttr.get(subject);
        const next = { ...(pose.effects ?? {}) };
        next[effect.name] = effect;
        PoseAttr.set(subject, { ...pose, effects: next });
    }


    private remove(effect: Effect, subject: Component) {
        const pose = PoseAttr.get(subject);
        const next = { ...(pose.effects ?? {}) };
        delete next[effect.name];
        PoseAttr.set(subject, { ...pose, effects: next });
    }
}
